// Webrtc plugin.
import { sessionActions } from '../interface/actions'
import { forwardAction } from '../../common/utils'

// Libraries.
import { takeEvery } from 'redux-saga/effects'
import { eventChannel } from 'redux-saga'

/**
 * Sets up event listeners for a Session's events.
 * The events are turned into actions and dispatched
 *    to be handled by redux elsewhere.
 * @method watchSessionEvents
 * @param  {Object} session The Session object.
 * @param  {Object} webRTC The webRTC stack.
 */
export default function * watchSessionEvents (session, webRTC) {
  /**
   * Create an event channel between the Session and redux-saga.
   * This "forces" the Session events into a redux-saga context.
   */
  const sessionChannel = eventChannel(emit => {
    return setListeners(session, emit)
  })
  // sessionChannel will emit the Session's events as actions, so dispatch
  //    them to be handled by sagas.
  // TODO: If the event is "Session gone", should close/unsubscribe the channel.
  yield takeEvery(sessionChannel, forwardAction)
}

/**
 * Function to set-up listeners for a Session object's events.
 * Contains the logic for how a webRTC event should be parsed
 *    before being sent elsewhere. Split into a stand-alone
 *    function in order to be re-usable.
 * @method setListeners
 * @param  {Object}   session A Session object.
 * @param  {Function} emit  Function that receives the parsed events.
 * @param  {Any}      [END='END'] The "end of lifetime" symbol.
 * @return {Function} The unsubscribe function.
 */
export function setListeners (session, emit, END = 'END') {
  // Session received a new track.
  const newTrack = ({ local, trackId }) => {
    /**
     * When a new track has been added,
     * update redux state's webrtc.session.localTracks/remoteTracks array
     * with the new trackId.
     * ('local' boolean will determine whether to update localTracks or remoteTracks)
     */
    emit(
      sessionActions.sessionNewTrack(session.id, {
        local,
        trackId
      })
    )
  }

  const trackEnded = ({ local, trackId, isUnsolicited }) => {
    /**
     * When a track has ended,
     * update redux state's webrtc.session.localTracks/remoteTracks array
     * by removing the specified trackId.
     * ('local' boolean will determine whether to update localTracks or remoteTracks)
     */
    emit(
      sessionActions.sessionTrackEnded(session.id, {
        local,
        trackId,
        isUnsolicited
      })
    )
  }

  const trackReplaced = ({ oldTrackId, trackId }) => {
    emit(
      sessionActions.sessionTrackReplaced(session.id, {
        oldTrackId,
        trackId
      })
    )
  }

  const iceConnectionStateChange = obj => {
    emit(
      sessionActions.sessionIceConnectionStateChange(session.id, {
        iceConnectionState: obj.iceConnectionState
      })
    )
  }

  const iceCandidateCollected = obj => {
    emit(sessionActions.sessionIceCandidateCollected(session.id, obj))
  }

  const iceGatheringStateChange = obj => {
    emit(sessionActions.sessionIceGatheringStateChange(session.id, obj))
  }

  const iceCollectionScheduledCheck = obj => {
    emit(sessionActions.sessionIceCollectionScheduledCheck(session.id, obj))
  }

  // Generic "something changed" handler.
  // TODO: Either use this for other events or remove it.
  // eslint-disable-next-line no-unused-vars
  const onChange = () => {
    const state = session.getState()

    emit(
      sessionActions.sessionChange(session.id, {
        id: state.id,
        localTracks: state.localTracks.map(track => track.id),
        remoteTracks: state.remoteTracks.map(track => track.id)
      })
    )
  }

  session.on('new:track', newTrack)
  session.on('track:ended', trackEnded)
  session.on('track:replaced', trackReplaced)
  session.on('peer:iceConnectionStateChange', iceConnectionStateChange)
  session.on('peer:iceGatheringStateChange', iceGatheringStateChange)
  session.on('peer:iceCandidateCollected', iceCandidateCollected)
  session.on('peer:iceCollectionScheduledCheck', iceCollectionScheduledCheck)

  const unsubscribe = () => {
    session.off('new:track', newTrack)
    session.off('track:ended', trackEnded)
    session.off('track:replaced', trackReplaced)
    session.off('peer:iceConnectionStateChange', iceConnectionStateChange)
    session.off('peer:iceGatheringStateChange', iceGatheringStateChange)
    session.off('peer:iceCandidateCollected', iceCandidateCollected)
    session.off('peer:iceCollectionScheduledCheck', iceCollectionScheduledCheck)
  }
  return unsubscribe
}
